import { useEffect } from "react";
import { createKeyboardTracker, getVisualViewport } from "./keyboardViewport";

// iOS keyboard dismissal leaves `position: fixed; bottom: 0` layers stranded.
//
// While the keyboard is open iOS pans the visual viewport inside the layout
// viewport. When it closes, the visual viewport grows back, but WebKit does not
// re-anchor fixed layers until the next document scroll: the layer keeps the
// position it had against the shifted viewport and sits too high. Any scroll
// fixes it, which a page too short to scroll can never produce.
//
// This reconciles the layer without waiting for the athlete:
//   1. measure where the layer's bottom edge is against the bottom of the
//      visual viewport (both in layout-viewport coordinates);
//   2. if they disagree while no keyboard is open, shift the layer by exactly
//      that difference (a CSS variable its `translate` reads);
//   3. drop the shift on the next document scroll, when WebKit re-anchors the
//      layer itself.
// It also asks for a 1px scroll round trip when the page can scroll, the same
// thing that fixes it by hand. Presentation only: no state, no writes.

const SETTLE_DELAYS_MS = [0, 120, 350, 700];
const TOLERANCE_PX = 1;
// Consumed by `.lz-v2-sticky-actions { translate: 0 var(...) }` (primitives.css).
export const SHIFT_PROPERTY = "--lz-v2-fixed-bottom-shift";
const EDITABLE_SELECTOR = "input, textarea, select, [contenteditable='true']";

function clearShift(element) {
  if (element.style.getPropertyValue(SHIFT_PROPERTY)) element.style.removeProperty(SHIFT_PROPERTY);
}

// Exported for tests: the pure decision.
export function fixedBottomShift({ layerBottom, viewportOffsetTop, viewportHeight, currentShift = 0 }) {
  const naturalBottom = layerBottom - currentShift;
  const visualBottom = viewportOffsetTop + viewportHeight;
  const shift = visualBottom - naturalBottom;
  return Math.abs(shift) > TOLERANCE_PX ? shift : 0;
}

function currentShift(element) {
  const value = Number.parseFloat(element.style.getPropertyValue(SHIFT_PROPERTY));
  return Number.isFinite(value) ? value : 0;
}

export default function useFixedBottomViewportSync(ref) {
  useEffect(() => {
    const viewport = getVisualViewport();
    const element = ref.current;
    if (!viewport || !element) return undefined;

    const measureKeyboard = createKeyboardTracker();
    let keyboardWasOpen = measureKeyboard(viewport).open;
    let timers = [];

    // Only a layer that really is `position: fixed; bottom: 0`: a consumer that
    // restyles it into the flow is left alone.
    const isBottomFixed = (node) => {
      const style = window.getComputedStyle(node);
      return style.position === "fixed" && Number.parseFloat(style.bottom) === 0;
    };

    const reconcile = () => {
      const node = ref.current;
      if (!node || !node.isConnected) return;
      if (!isBottomFixed(node)) {
        clearShift(node);
        return;
      }
      // With the keyboard open the layer belongs under it, as today.
      if (measureKeyboard(viewport).open) {
        clearShift(node);
        return;
      }
      const shift = fixedBottomShift({
        layerBottom: node.getBoundingClientRect().bottom,
        viewportOffsetTop: viewport.offsetTop,
        viewportHeight: viewport.height,
        currentShift: currentShift(node),
      });
      if (shift) node.style.setProperty(SHIFT_PROPERTY, `${shift}px`);
      else clearShift(node);
    };

    // The scroll that fixes it by hand, when the page can scroll at all.
    const nudgeDocumentScroll = () => {
      const root = document.documentElement;
      if (root.style.overflow === "hidden") return;
      const max = root.scrollHeight - window.innerHeight;
      if (max < 1) return;
      const step = window.scrollY < max ? 1 : -1;
      window.scrollBy(0, step);
      window.requestAnimationFrame(() => window.scrollBy(0, -step));
    };

    const settle = ({ nudge = false } = {}) => {
      timers.forEach(clearTimeout);
      if (nudge) nudgeDocumentScroll();
      // The viewport keeps moving for the length of the keyboard animation.
      timers = SETTLE_DELAYS_MS.map((delay) => setTimeout(reconcile, delay));
    };

    const onViewportResize = () => {
      const open = measureKeyboard(viewport).open;
      if (keyboardWasOpen && !open) settle({ nudge: true });
      else if (open) clearShift(ref.current || element);
      keyboardWasOpen = open;
    };
    const onFocusOut = (event) => {
      if (event.target?.matches?.(EDITABLE_SELECTOR)) settle();
    };
    // WebKit re-anchors fixed layers on a document scroll: the shift is stale.
    const onDocumentScroll = () => {
      const node = ref.current;
      if (node?.style.getPropertyValue(SHIFT_PROPERTY)) reconcile();
    };

    viewport.addEventListener("resize", onViewportResize);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("scroll", onDocumentScroll, { passive: true });
    return () => {
      timers.forEach(clearTimeout);
      viewport.removeEventListener("resize", onViewportResize);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("scroll", onDocumentScroll);
      clearShift(element);
    };
  }, [ref]);
}
